/**
 * Single source of truth for the service state machine (spec: service-state-machine).
 * Keyed by `catalogo` keys, never ids — ids are resolved to keys only at the
 * call boundary (backend EstadosServicioService) and back via resolverIdPorKey.
 */

export const ESTADOS_SERVICIO = [
  'ACTIVO',
  'ACTIVO_AL_CORTE',
  'SUSPENDIDO',
  'BAJA',
  'PENDIENTE',
  'BAJA_AL_CORTE',
] as const;

export type EstadoServicioKey = (typeof ESTADOS_SERVICIO)[number];

/** States a brand-new service may be created in (create is not a transition). */
export const ESTADOS_INICIALES = ['PENDIENTE', 'ACTIVO', 'ACTIVO_AL_CORTE'] as const;
export type EstadoInicialKey = (typeof ESTADOS_INICIALES)[number];

export type TriggerServicio = 'manual' | 'cutoff' | 'cascade' | 'reactivacion';

/** fechaFin effect of a transition: write ctx.fecha (today/cutoff), clear to null, null = no write. */
export type EfectoFechaFin = 'today' | 'cutoff' | 'clear' | null;

export interface TransicionDefinida {
  destino: EstadoServicioKey;
  efectoFechaFin: EfectoFechaFin;
}

/**
 * Transition table per spec, one entry per trigger. Every (origen, trigger)
 * pair includes the same→same no-op row; origins absent from a trigger are
 * not listed and only allow same→same (cascade AL_CORTE, cutoff non-AL_CORTE,
 * reactivacion non-BAJA).
 */
export const TRANSICIONES: Record<
  TriggerServicio,
  Partial<Record<EstadoServicioKey, TransicionDefinida[]>>
> = {
  manual: {
    ACTIVO: [
      { destino: 'ACTIVO', efectoFechaFin: null },
      { destino: 'SUSPENDIDO', efectoFechaFin: null },
      { destino: 'BAJA', efectoFechaFin: 'today' },
      { destino: 'BAJA_AL_CORTE', efectoFechaFin: null },
    ],
    SUSPENDIDO: [
      { destino: 'SUSPENDIDO', efectoFechaFin: null },
      { destino: 'ACTIVO', efectoFechaFin: null },
      { destino: 'ACTIVO_AL_CORTE', efectoFechaFin: null },
      { destino: 'BAJA', efectoFechaFin: 'today' },
      { destino: 'BAJA_AL_CORTE', efectoFechaFin: null },
    ],
    PENDIENTE: [
      { destino: 'PENDIENTE', efectoFechaFin: null },
      { destino: 'ACTIVO', efectoFechaFin: null },
      // Dead-end cerrado: un servicio creado en PENDIENTE que nunca se
      // activó no se puede borrar → puede dar de baja directo. Sin
      // BAJA_AL_CORTE: un servicio pendiente no tiene período que cortar.
      { destino: 'BAJA', efectoFechaFin: 'today' },
    ],
    BAJA_AL_CORTE: [
      { destino: 'BAJA_AL_CORTE', efectoFechaFin: null },
      { destino: 'BAJA', efectoFechaFin: 'today' },
      { destino: 'ACTIVO', efectoFechaFin: 'clear' },
    ],
    BAJA: [{ destino: 'BAJA', efectoFechaFin: null }],
    ACTIVO_AL_CORTE: [
      { destino: 'ACTIVO_AL_CORTE', efectoFechaFin: null },
      { destino: 'SUSPENDIDO', efectoFechaFin: null },
      { destino: 'BAJA', efectoFechaFin: 'today' },
      { destino: 'BAJA_AL_CORTE', efectoFechaFin: null },
      { destino: 'ACTIVO', efectoFechaFin: null },
    ],
  },
  cutoff: {
    BAJA_AL_CORTE: [
      { destino: 'BAJA_AL_CORTE', efectoFechaFin: null },
      { destino: 'BAJA', efectoFechaFin: 'cutoff' },
    ],
    ACTIVO_AL_CORTE: [
      { destino: 'ACTIVO_AL_CORTE', efectoFechaFin: null },
      { destino: 'ACTIVO', efectoFechaFin: null },
    ],
    ACTIVO: [{ destino: 'ACTIVO', efectoFechaFin: null }],
    SUSPENDIDO: [{ destino: 'SUSPENDIDO', efectoFechaFin: null }],
    PENDIENTE: [{ destino: 'PENDIENTE', efectoFechaFin: null }],
    BAJA: [{ destino: 'BAJA', efectoFechaFin: null }],
  },
  cascade: {
    ACTIVO: [
      { destino: 'ACTIVO', efectoFechaFin: null },
      { destino: 'BAJA', efectoFechaFin: 'today' },
    ],
    SUSPENDIDO: [
      { destino: 'SUSPENDIDO', efectoFechaFin: null },
      { destino: 'BAJA', efectoFechaFin: 'today' },
    ],
    PENDIENTE: [
      { destino: 'PENDIENTE', efectoFechaFin: null },
      { destino: 'BAJA', efectoFechaFin: 'today' },
    ],
    BAJA: [{ destino: 'BAJA', efectoFechaFin: null }],
    BAJA_AL_CORTE: [{ destino: 'BAJA_AL_CORTE', efectoFechaFin: null }],
    ACTIVO_AL_CORTE: [{ destino: 'ACTIVO_AL_CORTE', efectoFechaFin: null }],
  },
  reactivacion: {
    BAJA: [
      { destino: 'BAJA', efectoFechaFin: null },
      { destino: 'ACTIVO', efectoFechaFin: 'clear' },
    ],
    ACTIVO: [{ destino: 'ACTIVO', efectoFechaFin: null }],
    SUSPENDIDO: [{ destino: 'SUSPENDIDO', efectoFechaFin: null }],
    PENDIENTE: [{ destino: 'PENDIENTE', efectoFechaFin: null }],
    BAJA_AL_CORTE: [{ destino: 'BAJA_AL_CORTE', efectoFechaFin: null }],
    ACTIVO_AL_CORTE: [{ destino: 'ACTIVO_AL_CORTE', efectoFechaFin: null }],
  },
};

export const CATEGORIA_ESTADO_SERVICIO = 'ESTADO_SERVICIO';

export const KEY_BAJA = 'BAJA';

/**
 * Legal destinations of `origen` under `trigger`, including same→same.
 * Null or unknown origen falls back to ALL keys — UI fallback only, the
 * backend machine rejects those transitions.
 */
export function transicionesPermitidas(
  origen: EstadoServicioKey | null,
  trigger: TriggerServicio,
): EstadoServicioKey[] {
  if (origen === null || !ESTADOS_SERVICIO.includes(origen)) {
    return [...ESTADOS_SERVICIO];
  }
  return TRANSICIONES[trigger][origen]?.map((t) => t.destino) ?? [...ESTADOS_SERVICIO];
}
